import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { withStyles, Button, Divider, Select, MenuItem, CircularProgress, Typography, FormControl, InputLabel, FormGroup, FormControlLabel, Checkbox, Slider } from "@material-ui/core";
import { snackActions } from '../../../../sc-utilities/SnackBar';
import { verify, type } from '../../../../sc-utilities/CustomerUtilities';

const styles = theme => ({
    root: {
        height: 'auto',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    form: {
        width: '95%',
        margin: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    submit: {
        width: "120px",
        marginTop: "10px",
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    text: {
        borderRadius: '1px',
        marginRight: 10,
        color: theme.palette.buttonText.default
    },
    divider: {
        margin: theme.spacing(1),
    },
    formControl: {
        width: "100%"
    },
    buttons: {
        textAlign: 'right',
        padding: theme.spacing(2),
    }
});
function valueText(value) {
    return `${value}%`;
}

function PresetForm(props) {
    const { classes, methods, formSettings, initialValues, activeCustomer, loadProject, loading, handleSubmit, translations, customerPresetFilter, project, updatePresets, securazeInitialValues } = props;
    const { printers, grades, operatingSystems, orders, collies } = customerPresetFilter;

    return (
        <div className={classes.root}>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    await handleSubmit(values);
                    setSubmitting(false);
                }}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, isSubmitting, setFieldValue, handleChange, resetForm } = formikProps;
                    const gradeID = (grades ? grades : []).filter(grade => grade.value === values.GradeID).length > 0;
                    const operatingSystemID = (operatingSystems ? operatingSystems : []).filter(os => os.value === values.OperatingSystemID).length > 0;
                    const printerID = (printers ? printers : []).filter(printer => printer.value === values.PrinterID).length > 0;
                    const transportColliID = (orders ? orders : []).filter(el => el.OrderID === values.OrderID ? el.OrderID : []).filter(colli => colli?.TransportCollies.filter(el => el.TransportColliID === values.TransportColliID)).length > 0;
                    const colliID = (collies ? collies : []).filter(colli => colli?.value === values.ColliID).length > 0;
                    let reset = false;
                    const resetFn = () => {
                        reset = true;
                        resetForm();
                    }
                    return (
                        <form className={classes.form} onSubmit={e => {
                            e.preventDefault();
                            formikProps.submitForm();
                        }}>
                            <Button className={classes.text} variant="contained" disabled={!values.PresetsEnabled} color={'primary'} onClick={async () => {
                                let serverResult;
                                let ProjectID = project?.ProjectID;

                                if (ProjectID) {
                                    serverResult = await updatePresets({ Presets: securazeInitialValues, ProjectID: ProjectID, ActiveCustomerID: activeCustomer });
                                    if (serverResult) {
                                        loadProject({ ProjectID: ProjectID, ActiveCustomerID: activeCustomer });
                                    }
                                }
                                initialValues['PresetsEnabled'] = false;
                                if (securazeInitialValues) securazeInitialValues['PresetsEnabled'] = false;

                                resetFn();
                                if (serverResult?.status || reset) {
                                    return snackActions.snackBarComponent("Presets updated.", "success");
                                } else {
                                    return snackActions.snackBarComponent("Error: Failed to update presets", "error");
                                }
                            }}>
                                {translations['reset'] || 'Reset'}
                            </Button>
                            <Typography align='center' variant={"h5"}>{translations['preset_basic'] || 'Preset basic'}</Typography>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color={'primary'}
                                            margin={"dense"}
                                            name={"PresetsEnabled"}
                                            checked={!!values.PresetsEnabled || false}
                                            onChange={handleChange}
                                        />
                                    }
                                    label={`${translations['enable_preset'] || 'Enable preset'}`}
                                />
                            </FormGroup>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color={'primary'}
                                            margin={"dense"}
                                            name={"AutoShutdown"}
                                            disabled={!values.PresetsEnabled}
                                            checked={!!values.AutoShutdown || false}
                                            onChange={handleChange}
                                        />
                                    }
                                    label={`${translations['auto_shut_down'] || 'Auto shut down'}`}
                                />
                            </FormGroup>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color={'primary'}
                                            margin={"dense"}
                                            name={"AutoEnableRemoteAccess"}
                                            checked={!!values.AutoEnableRemoteAccess || false}
                                            disabled={!values.PresetsEnabled}
                                            onChange={handleChange}
                                        />
                                    }
                                    label={translations['auto_enable_remote_access'] || 'Auto enable remote access'}
                                />
                            </FormGroup>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color={'primary'}
                                            margin={"dense"}
                                            name={"AutoStartErasure"}
                                            checked={!!values.AutoStartErasure || false}
                                            onChange={handleChange}
                                            disabled={!values.PresetsEnabled}
                                        />
                                    }
                                    label={`${translations['autostart_erasure'] || 'Autostart erasure'}`}
                                />
                            </FormGroup>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color={'primary'}
                                            margin={"dense"}
                                            name={"AutoStartDiagnose"}
                                            checked={!!values.AutoStartDiagnose || false}
                                            disabled={!values.PresetsEnabled}
                                            onChange={handleChange}
                                        />
                                    }
                                    label={translations['auto_start_diagnose'] || 'Auto start diagnostic'}
                                />
                            </FormGroup>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color={'primary'}
                                            margin={"dense"}
                                            name={"AutoPrint"}
                                            checked={!!values.AutoPrint || false}
                                            onChange={handleChange}
                                            disabled={!values.PresetsEnabled}
                                        />
                                    }
                                    label={`${translations['auto_print_label'] || 'Auto print label'}`}
                                />
                            </FormGroup>

                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["order_select"] || "Please select order"}</InputLabel>
                                <Select
                                    name="OrderID"
                                    label={translations["order"] || 'Order'}
                                    margin={"dense"}
                                    value={values.OrderID}
                                    onChange={(event) => { setFieldValue('TransportColliID', ''); handleChange(event); return event.stopPropagation() }}
                                    disabled={!values.PresetsEnabled}
                                >
                                    {(orders ? orders : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["transport_colli_text"] || "Please select transport colli"}</InputLabel>
                                <Select
                                    name="TransportColliID"
                                    label={translations["transport_colli"] || 'Transport Colli'}
                                    margin={"dense"}
                                    value={transportColliID ? values.TransportColliID + '' : ''}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}
                                >
                                    {(orders?.length > 0 ? orders.filter(order => order.value === values.OrderID) : []).map(
                                        order => {
                                            return order.TransportCollies.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.displayValue}
                                                </MenuItem>

                                            ))
                                        }
                                    )}
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["colli_text"] || "Please select colli"}</InputLabel>
                                <Select
                                    name="ColliID"
                                    label={translations["colli"] || 'Colli'}
                                    margin={"dense"}
                                    value={colliID ? values.ColliID : ''}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}
                                >
                                    {(collies ? collies : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Divider className={classes.divider} variant="middle" />
                            <Typography align="center" variant={"h5"}>{translations['erasure_ssd'] || 'Erasure SSD'}</Typography>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations['wipe_method_ssd_text'] || "Please select ssd erasure method"}</InputLabel>
                                <Select
                                    name="MethodSSD"
                                    label={translations['wipe_method_ssd'] || 'SSD Erasure method'}
                                    margin={"dense"}
                                    value={values.MethodSSD || "dod_e"}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}

                                >
                                    {(methods ? methods : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["wipe_verify_ssd_text"] || "Please select ssd verify method"}</InputLabel>
                                <Select
                                    name="VerificationSSD"
                                    label={translations['VerificationSSD'] || 'Wipe verify ssd'}
                                    margin={"dense"}
                                    value={values.VerificationSSD || 'last'}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}
                                >
                                    {(verify ? verify : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {values.VerificationSSD === 'custom' ?
                                <>
                                    <InputLabel shrink>{translations["wipe_verify_ssd_number"] || "Please select number of rounds for SSD"}</InputLabel>
                                    <Slider
                                        name="CustomValueSSD"
                                        valueLabelFormat={valueText}
                                        onChange={(event, value) => setFieldValue('CustomValueSSD', value)}
                                        valueLabelDisplay="on"
                                        aria-labelledby="discrete-slider"
                                        value={values.CustomValueSSD}
                                        step={1}
                                        marks
                                        min={0}
                                        max={100}
                                    />  </> : <> </>}
                            <Divider className={classes.divider} variant="middle" />
                            <Typography align="center" variant={"h5"}>{translations['erasure_hdd'] || 'Erasure HDD'}</Typography>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations['wipe_method_hdd_text'] || "Please select hdd erasure method"}</InputLabel>
                                <Select
                                    name="MethodHDD"
                                    label={translations['wipe_method_hdd'] || 'HDD Erasure method'}
                                    margin={"dense"}
                                    value={values.MethodHDD || "dod_e"}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}

                                >
                                    {(methods ? methods : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["wipe_verify_hdd_text"] || "Please select hdd verify method"}</InputLabel>
                                <Select
                                    name="VerificationHDD"
                                    label={translations['VerificationHDD'] || 'Wipe verify hdd'}
                                    margin={"dense"}
                                    value={values.VerificationHDD || 'last'}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}
                                >
                                    {(verify ? verify : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {values.VerificationHDD === 'custom' ?
                                <>
                                    <InputLabel shrink>{translations["wipe_verify_hdd_number"] || "Please select number of rounds for HDD"}</InputLabel>
                                    <Slider
                                        name="CustomValueHDD"
                                        defaultValue={0}
                                        valueLabelFormat={valueText}
                                        onChange={(event, value) => setFieldValue('CustomValueHDD', value)}
                                        valueLabelDisplay="on"
                                        aria-labelledby="discrete-slider"
                                        value={values.CustomValueHDD}
                                        step={1}
                                        marks
                                        min={0}
                                        max={100}
                                    />  </> : <> </>}

                            <Divider className={classes.divider} variant="middle" />
                            <br />
                            <Typography align="center" variant={"h5"}>{translations['erasure_mobile'] || 'Erasure Mobile'}</Typography>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations['wipe_method_mobile_text'] || "Please select type erasure method mobile"}</InputLabel>
                                <Select
                                    name="TypeMobile"
                                    label={translations['wipe_method_mobile'] || 'Mobile Erasure method'}
                                    margin={"dense"}
                                    value={values.TypeMobile || "dod_e"}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}

                                >
                                    {(type ? type : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations['wipe_method_mobile_text'] || "Please select mobile erasure method"}</InputLabel>
                                <Select
                                    name="MethodMobile"
                                    label={translations['wipe_method_mobile'] || 'Mobile Erasure method'}
                                    margin={"dense"}
                                    value={values.MethodMobile || "dod_e"}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}

                                >
                                    {(methods ? methods : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["wipe_verify_mobile_text"] || "Please select mobile verify method"}</InputLabel>
                                <Select
                                    name="VerificationMobile"
                                    label={translations['VerificationMobile'] || 'Wipe verify mobile'}
                                    margin={"dense"}
                                    value={values.VerificationMobile || 'last'}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}
                                >
                                    {(verify ? verify : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {values.VerificationMobile === 'custom' ?
                                <>
                                    <InputLabel shrink>{translations["wipe_verify_mobile_number"] || "Please select number of rounds for Mobile"}</InputLabel>
                                    <Slider
                                        name="CustomValueMobile"
                                        defaultValue={0}
                                        valueLabelFormat={valueText}
                                        onChange={(event, value) => setFieldValue('CustomValueMobile', value)}
                                        valueLabelDisplay="on"
                                        aria-labelledby="discrete-slider"
                                        value={values.CustomValueMobile}
                                        step={1}
                                        marks
                                        min={0}
                                        max={100}
                                    />  </> : <><br></br> </>}
                            <Divider className={classes.divider} variant="middle" />
                            <br />
                            <Typography align='center' variant={"h5"}>{translations['preset_advanced'] || 'Presets advanced'}</Typography>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["printers_text"] || "Please select printer"}</InputLabel>
                                <Select
                                    name="PrinterID"
                                    label={translations["printers"] || 'Printers'}
                                    margin={"dense"}
                                    value={printerID ? values.PrinterID : ''}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}
                                >
                                    {(printers ? printers : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["grade_text"] || "Please select grade"}</InputLabel>
                                <Select
                                    name="GradeID"
                                    label={translations['grade'] || 'Grade'}
                                    margin={"dense"}
                                    defaultValue={''}
                                    value={gradeID ? values.GradeID : ''}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}

                                >
                                    {(grades ? grades : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["operating_system_text_select"] || "Please select operating system"}</InputLabel>
                                <Select
                                    name="OperatingSystemID"
                                    label={translations["operating_system"] || 'Operating System'}
                                    margin={"dense"}
                                    value={operatingSystemID ? values.OperatingSystemID : ''}
                                    onChange={handleChange}
                                    disabled={!values.PresetsEnabled}

                                >
                                    {(operatingSystems ? operatingSystems : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <div className={classes.buttons} >
                                {formSettings.ButtonSubmit ? (
                                    <Button
                                        className={classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        disabled={isSubmitting || loading}
                                    >
                                        { isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </div >
    );
}

export default withStyles(styles, { withTheme: true })(PresetForm);
